// Payment Method section on the public proposal page
// (Sales::ProposalDocument::PaymentComponent). Three server-rendered panes it
// moves between:
//
//   capturedWrap -- a method is on file (chip + "use a different method")
//   savedWrap    -- an existing client's saved Chargebee sources (radios)
//   chooserWrap  -- Chargebee's embedded payment component for a new method
//
// Capturing goes: fetch a payment intent (the server mints it against the
// client's Chargebee customer, creating a checkout shell customer for a new
// prospect) -> the component authorizes it -> POST the intent id back and the
// server vaults it as a payment source. Picking a saved source is a single
// POST. Either way the server records a Sales::PaymentMethod and this
// controller dispatches `sales-payment:captured` so the signing section can
// unlock itself (ISSUE-3823) -- no DOM reaching across sections from here, and
// the signature endpoint re-checks server-side regardless.
//
// The chooser mounts lazily: mounting mints a Chargebee intent (and a shell
// customer), so a bounce visitor who never scrolls this far creates neither,
// and the intent's ~30-minute life starts closer to when it is used.
// ISSUE-3822.
import { Controller } from '@hotwired/stimulus'

import createChargebeePayment, { declineMessageFor } from '@/utils/chargebee_payment_component'

const MOUNT_SELECTOR = '#sales-payment-element'

export default class extends Controller {
  static targets = [
    'capturedWrap',
    'capturedName',
    'capturedNote',
    'savedWrap',
    'savedRadio',
    'savedAchAuthorization',
    'useSavedButton',
    'chooserWrap',
    'paymentElement',
    'surchargeNotice',
    'achAuthorization',
    'errors',
    'saveButton',
    'saveButtonText',
    'saveSpinner'
  ]

  static values = {
    site: String,
    publishableKey: String,
    intentUrl: String,
    methodUrl: String,
    surchargePercentLabel: String,
    captured: Boolean,
    capturedSurchargeable: Boolean
  }

  connect() {
    if (this.capturedValue) return
    if (this.hasChooserWrapTarget && !this.chooserWrapTarget.classList.contains('d-none')) {
      this.observeChooser()
    }
  }

  disconnect() {
    this.stopObservingChooser()
    this.resetPayment()
  }

  observeChooser() {
    if (!('IntersectionObserver' in window)) {
      this.initializePayment()
      return
    }

    this.chooserObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.stopObservingChooser()
          this.initializePayment()
        }
      },
      { rootMargin: '300px' }
    )
    this.chooserObserver.observe(this.chooserWrapTarget)
  }

  stopObservingChooser() {
    if (this.chooserObserver) {
      this.chooserObserver.disconnect()
      this.chooserObserver = null
    }
  }

  // "Use a different method": reveal the pickers. The previous method stays
  // active server-side until a replacement lands, so signing stays unlocked.
  change() {
    this.capturedWrapTarget.classList.add('d-none')
    if (this.hasSavedWrapTarget) {
      this.savedWrapTarget.classList.remove('d-none')
    } else {
      this.showChooser()
    }
  }

  showChooser() {
    if (this.hasSavedWrapTarget) this.savedWrapTarget.classList.add('d-none')
    this.chooserWrapTarget.classList.remove('d-none')
    this.initializePayment()
  }

  showSaved() {
    this.chooserWrapTarget.classList.add('d-none')
    this.savedWrapTarget.classList.remove('d-none')
    this.hideError()
  }

  // A saved bank account shows the same debit authorization the new-account
  // form does, before the customer can use it, since this proposal's debits
  // need their own authorization.
  savedPicked() {
    this.useSavedButtonTarget.disabled = false
    if (this.hasSavedAchAuthorizationTarget) {
      const bank = this.checkedSavedRadio()?.dataset.paymentType === 'direct_debit'
      this.savedAchAuthorizationTarget.classList.toggle('d-none', !bank)
    }
  }

  async useSaved() {
    const radio = this.checkedSavedRadio()
    if (!radio) return

    this.useSavedButtonTarget.disabled = true
    try {
      const result = await this.postJson(this.methodUrlValue, { payment_source_id: radio.value })
      this.markCaptured(result.payment_method)
    } catch (error) {
      this.useSavedButtonTarget.disabled = false
      this.showError(error.message)
    }
  }

  // `payment` is held in a local across both awaits: a recovery that replaces
  // the component mid-flight must not have this continuation mount into, or
  // tear down, its successor.
  async initializePayment() {
    if (this.payment) return

    const payment = createChargebeePayment({
      site: this.siteValue,
      publishableKey: this.publishableKeyValue,
      mountSelector: MOUNT_SELECTOR,
      onSuccess: this.componentAuthorized.bind(this),
      onError: this.componentFailed.bind(this),
      onPaymentMethodChange: this.methodTypeChanged.bind(this)
    })
    this.payment = payment

    try {
      const paymentIntent = await this.createPaymentIntent()
      if (this.payment !== payment) return

      await payment.mount(paymentIntent)
    } catch (error) {
      console.error('[SalesPayment] Initialization error:', error)
      if (this.payment !== payment) return

      this.resetPayment()
      this.showError('We could not load the payment form. Please refresh the page and try again.')
    }
  }

  methodTypeChanged(paymentMethod) {
    const card = paymentMethod === 'card'
    this.surchargeNoticeTarget.classList.toggle('d-none', !card)
    this.achAuthorizationTarget.classList.toggle('d-none', card)
  }

  async save(event) {
    event.preventDefault()
    if (!this.payment) return

    this.setSaving(true)
    this.hideError()
    try {
      if (!(await this.payment.validate())) {
        this.setSaving(false)
        this.showError('Please complete the highlighted payment details and try again.')
        return
      }
      await this.payment.confirm()
    } catch (error) {
      console.error('[SalesPayment] Authorization error:', error)
      this.recoverFromComponentError(error)
    }
  }

  componentAuthorized(paymentIntent) {
    this.postJson(this.methodUrlValue, { payment_intent_id: paymentIntent.id })
      .then((result) => this.markCaptured(result.payment_method))
      .catch((error) => {
        this.showError(error.message)
        this.setSaving(false)
      })
  }

  componentFailed(error) {
    console.error('[SalesPayment] Payment error:', error)
    this.recoverFromComponentError(error)
  }

  // Any confirm() failure paints the component's own raw gateway banner
  // ("Your card was declined.; code: card_declined;") inside its iframe, and
  // Chargebee offers no supported way to suppress or restyle it -- the whole
  // component is one cross-origin iframe, so it cannot be hidden from out here
  // either. Remounting with a fresh intent is the only way to clear it, so
  // every recovery path remounts and the customer reads exactly one message:
  // ours. This also covers an expired intent (the reader who walked away
  // mid-checkout), which would otherwise fail forever without a reload.
  //
  // One gateway failure can arrive twice -- the component fires onError and
  // confirm() rejects -- so the guard keeps that to a single remount, and a
  // single payment intent.
  async recoverFromComponentError(error) {
    if (this.recovering) return

    this.recovering = true
    try {
      this.setSaving(false)
      this.resetPayment()
      this.showError(declineMessageFor(error))
      await this.initializePayment()
    } finally {
      this.recovering = false
    }
  }

  async createPaymentIntent() {
    const result = await this.postJson(this.intentUrlValue, {})
    return result.payment_intent
  }

  // No CSRF header: the capture endpoints skip forgery protection, the share
  // token in the path is the credential, and the page carries no session.
  async postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })

    let result = null
    try {
      result = await response.json()
    } catch {
      // Non-JSON error page -- fall through to the generic message.
    }
    if (!response.ok || !result || result.success === false) {
      throw new Error(result?.error || 'Something went wrong. Please try again.')
    }
    return result
  }

  markCaptured(paymentMethod) {
    this.capturedNameTarget.textContent = paymentMethod.display_name
    this.capturedNoteTarget.textContent = paymentMethod.surchargeable ? this.surchargeNote : ''
    this.capturedWrapTarget.classList.remove('d-none')
    if (this.hasSavedWrapTarget) this.savedWrapTarget.classList.add('d-none')
    this.chooserWrapTarget.classList.add('d-none')
    // The authorized intent was consumed by the vault -- tear the component
    // down so "use a different method" remounts with a fresh intent instead of
    // reusing a dead one.
    this.resetPayment()
    this.setSaving(false)
    this.hideError()
    this.capturedValue = true
    this.capturedSurchargeableValue = !!paymentMethod.surchargeable
    this.dispatch('captured', {
      detail: { surchargeable: !!paymentMethod.surchargeable },
      prefix: 'sales-payment'
    })
  }

  // Kept in step with the same sentence the component renders server-side.
  get surchargeNote() {
    return (
      `Credit card payments carry a ${this.surchargePercentLabelValue} processing fee; ` +
      'use a debit card or bank account to avoid it.'
    )
  }

  resetPayment() {
    if (!this.payment) return

    this.payment.tearDown()
    this.payment = null
  }

  checkedSavedRadio() {
    return this.savedRadioTargets.find((radio) => radio.checked)
  }

  setSaving(saving) {
    this.saveButtonTarget.disabled = saving
    this.saveButtonTextTarget.textContent = saving ? 'Saving...' : 'Save payment method'
    this.saveSpinnerTarget.classList.toggle('d-none', !saving)
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove('d-none')
  }

  hideError() {
    this.errorsTarget.classList.add('d-none')
  }
}
